import React from 'react';
import Markdown, {hasParents} from 'react-native-markdown-renderer';
import {StyleSheet, View, Text} from 'react-native';

import {Props} from './model';
import {useTheme} from '../../theme';
import {useDeviceSize, DeviceSize} from '../../theme/helpers';
import {images} from '../../../assets/icons';
import {getFontWeight} from '../../theme/default';

export default ({children, listColumns = 1, textStyle, testID}: Props) => {
  const {CheckMark} = images;
  const theme = useTheme();
  const deviceSize = useDeviceSize();
  const defaultTextStyle = theme.fonts.h5Light;

  const ruleStyles = StyleSheet.create({
    checkMark: {
      paddingTop: 16,
    },
  });

  // source file:https://github.com/mientjan/react-native-markdown-renderer/blob/master/src/lib/renderRules.js
  const renderRules = {
    paragraph: (node: any, child: any, parent: any, styles: any) => (
      <View key={node.key} style={styles.paragraph} testID={testID}>
        {child}
      </View>
    ),
    list_item: (node: any, child: any, parent: any, styles: any) => {
      if (hasParents(parent, 'bullet_list')) {
        return (
          <View key={node.key} style={styles.listUnorderedItem}>
            <View style={styles.listUnorderedItemIcon}>
              <View style={ruleStyles.checkMark}>
                <CheckMark width={18} height={14} color={theme.supportService.questionAccordionCheckMarkColor} />
              </View>
            </View>
            <View style={[styles.listItem]}>{child}</View>
          </View>
        );
      }

      if (hasParents(parent, 'ordered_list')) {
        return (
          <View key={node.key} style={styles.listOrderedItem}>
            <View style={[styles.listItem]}>
              <Text
                style={{fontWeight: getFontWeight('bold'), fontFamily: theme.fonts.fontFamily.bold, lineHeight: 20}}>
                {node.index + 1}
                {node.markup}. {child}
              </Text>
            </View>
          </View>
        );
      }

      return (
        <View key={node.key} style={[styles.listItem]}>
          {child}
        </View>
      );
    },
  };

  const mobileH = {...theme.fonts.action, marginBottom: 6};
  const markdownStyles = StyleSheet.create({
    root: {},
    view: {},
    codeBlock: {
      borderWidth: 1,
      borderColor: theme.color.borderColor,
      backgroundColor: theme.color.backgroundMenuColor,
      padding: 10,
      borderRadius: 4,
    },
    codeInline: {
      borderWidth: 1,
      borderColor: theme.color.borderColor,
      backgroundColor: theme.color.backgroundMenuColor,
      padding: 10,
      borderRadius: 4,
    },
    del: {
      backgroundColor: theme.color.backgroundMenuColor,
    },
    em: {
      fontStyle: 'italic',
    },
    headingContainer: {
      flexDirection: 'row',
    },
    heading: {},
    heading1: deviceSize === DeviceSize.desktop ? theme.fonts.h1 : mobileH,
    heading2: deviceSize === DeviceSize.desktop ? theme.fonts.h2 : mobileH,
    heading3: deviceSize === DeviceSize.desktop ? theme.fonts.h3 : mobileH,
    heading4: deviceSize === DeviceSize.desktop ? theme.fonts.h4 : mobileH,
    heading5: deviceSize === DeviceSize.desktop ? theme.fonts.h5 : mobileH,
    heading6: deviceSize === DeviceSize.desktop ? theme.fonts.h6 : mobileH,
    hr: {
      backgroundColor: theme.color.primaryBlackColor,
      height: 1,
    },
    blockquote: {
      paddingHorizontal: 20,
      paddingVertical: 10,
      margin: 20,
      backgroundColor: theme.color.primaryNonGreyBackground,
    },
    inlineCode: {
      borderRadius: 3,
      borderWidth: 1,
      fontFamily: 'Courier',
      fontWeight: 'bold',
    },
    list: {
      marginTop: 10,
    },
    listItem: {
      flex: 1,
      flexWrap: 'wrap',
      // backgroundColor: 'green',
    },
    listUnordered: {
      ...(listColumns === 2
        ? {
            flexDirection: 'row',
            flexWrap: 'wrap',
          }
        : {}),
    },

    listUnorderedItem: {
      ...(listColumns === 2 ? {flexBasis: '50%'} : {}),
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },

    listUnorderedItemText: {
      fontSize: 20,
      lineHeight: 20,
    },

    listOrdered: {},
    listOrderedItem: {
      flexDirection: 'row',
    },
    listOrderedItemText: {
      fontWeight: getFontWeight('bold'),
      fontFamily: theme.fonts.fontFamily.bold,
      lineHeight: 20,
    },
    paragraph: {
      marginTop: 10,
      marginBottom: 10,
      flexWrap: 'wrap',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    },
    hardbreak: {
      width: '100%',
      height: 1,
    },
    strong: {
      fontWeight: getFontWeight('bold'),
      fontFamily: theme.fonts.fontFamily.bold,
    },
    table: {
      borderWidth: 1,
      borderColor: theme.color.primaryBlackColor,
      borderRadius: 3,
    },
    tableHeader: {},
    tableHeaderCell: {
      flex: 1,
      // color: theme.color.primaryBlackColor,,
      padding: 5,
      // backgroundColor: 'green',
    },
    tableRow: {
      borderBottomWidth: 1,
      borderColor: theme.color.primaryBlackColor,
      flexDirection: 'row',
    },
    tableRowCell: {
      flex: 1,
      padding: 5,
    },
    text:
      deviceSize === DeviceSize.desktop
        ? {...(textStyle ?? defaultTextStyle), color: theme.color.textColor}
        : {...theme.fonts.bodySmallLight, color: theme.color.textColor},
    strikethrough: {
      textDecorationLine: 'line-through',
    },
    link: {
      color: theme.color.primaryColor,
    },
    blocklink: {
      flex: 1,
      borderColor: theme.color.primaryBlackColor,
      borderBottomWidth: 1,
    },
    u: {
      borderColor: theme.color.primaryBlackColor,
      borderBottomWidth: 1,
    },
    image: {
      flex: 1,
    },
  });

  return (
    <Markdown style={markdownStyles} rules={renderRules}>
      {children}
    </Markdown>
  );
};
